// Mobile QA viewer for a project's paint systems. The project is chosen at the top.
import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Page, Block, BlockTitle, List, ListItem, AccordionContent, Preloader, Searchbar } from "framework7-react";

import AppNavbar from "modules/1_layout/mobile/AppNavbar";
import HasRole from "modules/5_view_helpers/HasRole";
import NotAuthorized from "modules/5_view_helpers/mobile/NotAuthorized";
import SelectProject from "modules/projects/mobile/select";
import { setCurrentProject } from "modules/current_user/actions";

import { getProjectTechnicalDetailsPaintSystems } from "../actions";

function mapStateToProps(state) {
  const current_project_id = state.current_user?.current_project_id;
  return {
    current_project_id,
    project: state.projects?.[current_project_id],
    project_technical_details_paint_systems: state.project_technical_details_paint_systems,
  };
}

const ProjectTechnicalDetailsPaintSystemsMobileIndex = (props) => {
  const { current_project_id, project, project_technical_details_paint_systems } = props;
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  const readRole = HasRole({ resource_type: "Project::TechnicalDetails::PaintSystem", name: ["read", "moderator"] }, true);

  useEffect(() => {
    if (!current_project_id || !readRole) return;

    setLoading(true);
    props.getProjectTechnicalDetailsPaintSystems(current_project_id).finally(() => {
      setLoading(false);
    });
  }, [current_project_id]);

  if (!readRole) {
    return <NotAuthorized />;
  }

  const paint_systems = _.orderBy(
    _.filter(project_technical_details_paint_systems, (paint_system) => String(paint_system.project_id) === String(current_project_id)),
    "id"
  );
  const q = _.toLower(_.trim(query));
  const visible = q
    ? _.filter(paint_systems, (paint_system) => {
        const haystack = _.toLower(_.join([paint_system.display_code, paint_system.notes, paint_system.systems_sentence], " "));
        return _.includes(haystack, q);
      })
    : paint_systems;

  return (
    <Page>
      <AppNavbar title="Paint Systems" />

      <SelectProject
        onChange={({ target: { value } }) => {
          props.setCurrentProject(value);
        }}
        value={current_project_id}
      />

      {loading && <Preloader />}
      {!loading && (
        <Fragment>
          <Searchbar customSearch onSearchbarSearch={(_searchbar, value) => setQuery(value || "")} onSearchbarClear={() => setQuery("")} />
          <BlockTitle>{project?.display_name ? `${project.display_name} - Paint Systems` : "Paint Systems"}</BlockTitle>
          {_.isEmpty(visible) && (
            <List>
              <ListItem>{current_project_id ? "No paint systems" : "Select a project"}</ListItem>
            </List>
          )}
          <List accordionList>
            {_.map(visible, (paint_system) => (
              <PaintSystemRow key={paint_system.id} paint_system={paint_system} />
            ))}
          </List>
        </Fragment>
      )}
    </Page>
  );
};

export default connect(mapStateToProps, {
  getProjectTechnicalDetailsPaintSystems,
  setCurrentProject,
})(ProjectTechnicalDetailsPaintSystemsMobileIndex);

const PaintSystemRow = ({ paint_system }) => {
  const coats = _.filter(paint_system.coats_attributes, (coat) => !coat.blast_profile);

  return (
    <ListItem accordionItem title={paint_system.display_code} after={paint_system.approved ? "Approved" : "Not approved"}>
      <AccordionContent>
        <Block>
          <div>Please note: {paint_system.display_code}X on the drawings denotes there are some non coated areas</div>
          <div className="margin-top">
            {paint_system.is_multi_system ? (
              <span>
                {paint_system.display_code} is a multi paint system consisting of: <b>{paint_system.systems_sentence}</b>
              </span>
            ) : (
              <span>
                {paint_system.display_code} is a {paint_system.code_details_name} system. Surface preparation: {paint_system.surface_prep_name}
              </span>
            )}
          </div>
          {paint_system.notes && (
            <div className="margin-top">
              <i>Notes: {paint_system.notes}</i>
            </div>
          )}
          {!paint_system.is_multi_system && (
            <Fragment>
              <div className="margin-top">
                <b>Coats</b>
              </div>
              {_.map(coats, (coat) => (
                <div key={coat.id} className="margin-top">
                  <div>
                    <b>Coat {coat.number}</b> {coat.display_name}
                  </div>
                  <div>Colour: {coat.color || "—"}</div>
                  <div>DFT: {coat.dft_by_sections ? "By section sizes" : `${coat.dft}µ`}</div>
                  <div>Location: {coat.location_name}</div>
                  {coat.dft_by_sections &&
                    _.map(coat.sections_attributes, (section) => (
                      <div key={section.id} className="padding-left">
                        {section.strumis_section_type} &gt; {section.strumis_section_size}: {section.dft}µ
                      </div>
                    ))}
                </div>
              ))}
              <div className="margin-top">
                <b>Treatment grade: {paint_system.treatment_grade_name}</b>
              </div>
            </Fragment>
          )}
          <div className="margin-top">
            <b>Approved by: {paint_system.approved ? paint_system.approved_by_name : "Not approved yet"}</b>
          </div>
        </Block>
      </AccordionContent>
    </ListItem>
  );
};
